import { MessageSquare, Mic, MicOff, Paperclip, Square, Volume2, VolumeX } from "lucide-react";
import { cn } from "@/lib/utils";
import InfoPopover from "./InfoPopover";
import { MagicalIconButton } from "./MagicalIconButton";
import { MoreActionsPopover } from "./MoreActionsPopover";
import SummaryCaptureButton from "./SummaryCaptureButton";

import type { VoiceSessionActionRowProps } from "@/types/voice/voice-session-action-row";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
export const VoiceSessionActionRow = ({
  hidden = false,
  brandColor,
  isMobile,
  isTranscriptFeatureEnabled,
  voiceConnected,
  onEndConversation,
  isTextOnlyMode = false,
  isMicMuted,
  onToggleMicMute,
  isAgentVoiceMuted,
  onToggleAgentVoice,
  isPaused = false,
  onTogglePause,
  onOpenChat,
  chatOpen = false,
  hasResources = false,
  onOpenResources,
  infoTitle,
  infoDescription,
  infoLinks,
  qrLogoUrl,
  secondaryColor,
  showSummaryCapture = true,
  showInfoActions = true,
  onOpenDetails,
  onAddImage,
  onCallGenie,
  onEmailGenie,
  emailActionLabel,
  onEmailSummary,
  emailSummaryLabel,
  addToHomeScreen,
  onTalkToHuman,
  agentId,
  agentUserId,
  onSummaryCaptured,
  actionButtonStyle,
}: VoiceSessionActionRowProps) => {
  const sizeScale = actionButtonStyle?.sizeScale ?? 1;
  const scaled = (base: number) => Math.round(base * sizeScale);

  if (hidden) return null;
  if (voiceConnected && onEndConversation) {
    const pillButtonPx = scaled(36);
    const stopButtonPx = scaled(44);
    return (
      <div className="mt-6 flex w-full justify-center px-3">
        <div
          role="group"
          aria-label="Conversation controls"
          className="flex items-center gap-1.5 rounded-full p-1"
          style={{ background: `${brandColor}0d` }}
        >
          {!isTextOnlyMode && (
            <button
              type="button"
              onClick={onToggleMicMute}
              aria-label={isMicMuted ? "Unmute microphone" : "Mute microphone"}
              aria-pressed={isMicMuted}
              title={isMicMuted ? "Unmute microphone" : "Mute microphone"}
              className="flex items-center justify-center rounded-full transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2"
              style={{
                width: pillButtonPx,
                height: pillButtonPx,
                color: isMicMuted ? "#FFFFFF" : brandColor,
                background: isMicMuted ? brandColor : "transparent",
              }}
            >
              {isMicMuted ? (
                <MicOff size={scaled(16)} aria-hidden="true" />
              ) : (
                <Mic size={scaled(16)} aria-hidden="true" />
              )}
            </button>
          )}
          {!isTextOnlyMode && isTranscriptFeatureEnabled && (
            <button
              type="button"
              onClick={onOpenChat}
              aria-label={chatOpen ? "Hide chat" : "Show chat"}
              aria-pressed={chatOpen}
              title={chatOpen ? "Hide chat" : "Show chat"}
              className="flex items-center justify-center rounded-full transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2"
              style={{
                width: pillButtonPx,
                height: pillButtonPx,
                color: chatOpen ? "#FFFFFF" : brandColor,
                background: chatOpen ? brandColor : "transparent",
              }}
            >
              <MessageSquare size={scaled(16)} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={onEndConversation}
            aria-label="End conversation"
            title="End conversation"
            className="flex items-center justify-center rounded-full transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2"
            style={{
              width: stopButtonPx,
              height: pillButtonPx,
              color: brandColor,
              background: `linear-gradient(135deg, ${brandColor}20, ${secondaryColor || brandColor}14)`,
            }}
          >
            <Square size={scaled(14)} fill="currentColor" aria-hidden="true" />
          </button>
          <MoreActionsPopover
            brandColor={brandColor}
            secondaryColor={secondaryColor}
            size={pillButtonPx}
            hideLabel
            placement={isMobile && chatOpen ? "bottom" : "top"}
            onAddImage={onAddImage}
            onTogglePause={onTogglePause}
            isPaused={isPaused}
            onToggleAgentVoice={isTextOnlyMode ? undefined : onToggleAgentVoice}
            isAgentVoiceMuted={isAgentVoiceMuted}
            onOpenChat={!isTextOnlyMode && isTranscriptFeatureEnabled && !chatOpen ? onOpenChat : undefined}
            onOpenResources={hasResources ? onOpenResources : undefined}
            onCallGenie={onCallGenie}
            onEmailGenie={onEmailGenie}
            emailActionLabel={emailActionLabel}
            onEmailSummary={onEmailSummary}
            onTalkToHuman={onTalkToHuman}
            emailSummaryLabel={emailSummaryLabel}
            addToHomeScreen={addToHomeScreen}
            actionButtonStyle={{ ...actionButtonStyle, idleBackgroundColor: "transparent" }}
          />
        </div>
      </div>
    );
  }

  const showChat = isTranscriptFeatureEnabled;
  // Every Genie can be handed a photo, so this is gated on the callback alone.
  // Gating it on isTranscriptFeatureEnabled would hide it from any Genie whose
  // ElevenLabs config does not emit user_transcript, which is not the deal.
  const showAddImage = typeof onAddImage === "function";
  const showPause = voiceConnected && typeof onTogglePause === "function";
  // Language-switching is a pre-conversation-only action, folded into the same
  // overflow slot so the pre-connect row reads as "Type instead + More"
  // instead of a separate permanent Language button.
  const showLanguage = !voiceConnected;
  // Add photo + pause + language are secondary, occasional actions — neither is
  // a caller's primary need (start/stop, mute, switch to text) — so they share
  // a single "More" overflow slot instead of separate permanent row buttons.
  // Add to home screen is phones only, so it can't be More's only item on desktop.
  const showMore = showAddImage || showPause || showLanguage || !!onCallGenie || !!onEmailGenie || !!onEmailSummary || !!onTalkToHuman || (isMobile && !!addToHomeScreen);
  // Resources stays available mid-call, but pre/post it folds into the Details
  // panel when this surface delegates its info affordances (showInfoActions=false).
  const showResources = hasResources && typeof onOpenResources === "function" && (voiceConnected || showInfoActions);
  // About + "Get a summary" are pre/post-conversation only. When the Details
  // panel owns them, drop them from the row so the start screen stays calm.
  const showInfo = showInfoActions && !voiceConnected;
  const showSummary = showInfoActions && showSummaryCapture && !voiceConnected && !!agentId;
  // The consolidated Details entry — folded into the row (vs. a floating edge
  // tab) on surfaces like mobile. Pre/post-conversation only, like About.
  const showDetails = typeof onOpenDetails === "function" && !voiceConnected;
  // Extra slots need tighter spacing so 4-5 buttons still fit on mobile.
  const tight = showResources || showSummary || showDetails || showMore;
  const rowGap = voiceConnected
    ? showPause || tight
      ? "gap-4 sm:gap-6"
      : showChat
        ? "gap-6 sm:gap-8"
        : "gap-8 sm:gap-10"
    : tight
      ? "gap-6 sm:gap-8"
      : showChat
        ? "gap-8 sm:gap-10"
        : "gap-14 sm:gap-20";

  return (
    <div className="mt-4 lg:mt-8 flex w-full justify-center px-3">
      <div className={cn("flex flex-row items-end justify-center", rowGap)}>
        {/* Slot 1: Mic — mid-conversation only. Pre-connect, language-switch
            moved into the More overflow (see showLanguage below), so this
            slot is simply absent and the row starts with Type instead. */}
        {voiceConnected && (
          <MagicalIconButton
            onClick={() => void onToggleMicMute()}
            brandColor={brandColor}
            size={scaled(36)}
            ariaLabel={isMicMuted ? "Unmute microphone" : "Mute microphone"}
            label={isMicMuted ? "Unmute mic" : "Mute mic"}
            activeState={isMicMuted ? "danger" : null}
            hoverHint={isMicMuted ? "Speak to your Genie" : "Mute your microphone"}
            hintPlacement="top"
            idleBackgroundColor={actionButtonStyle?.idleBackgroundColor}
            activeBackgroundColor={actionButtonStyle?.activeBackgroundColor}
            iconColorOverride={actionButtonStyle?.iconColor}
          >
            {isMicMuted ? <MicOff size={scaled(16)} strokeWidth={2} /> : <Mic size={scaled(16)} strokeWidth={2} />}
          </MagicalIconButton>
        )}

        {/* Slot 2 (center, larger): Chat */}
        {showChat ? (
          <MagicalIconButton
            onClick={onOpenChat}
            brandColor={brandColor}
            size={scaled(48)}
            ariaLabel={chatOpen ? "Hide text input" : "Type instead"}
            label="Type instead"
            activeState={chatOpen ? "active" : null}
            hoverHint={chatOpen ? "Hide text input" : "Type to your Genie"}
            hintPlacement="top"
            idleBackgroundColor={actionButtonStyle?.idleBackgroundColor}
            activeBackgroundColor={actionButtonStyle?.activeBackgroundColor}
            iconColorOverride={actionButtonStyle?.iconColor}
          >
            <MessageSquare size={scaled(22)} strokeWidth={2} />
          </MagicalIconButton>
        ) : null}

        {/* Slot: More — secondary, occasional actions (add a photo, pause the
            conversation). Neither is a primary need for a caller/demo viewer
            (start/stop, mute, switch to text), so both share one overflow
            slot instead of two permanent row buttons. Available before and
            during a conversation; pause only ever applies mid-call. */}
        {showMore && (
          <MoreActionsPopover
            brandColor={brandColor}
            secondaryColor={secondaryColor}
            size={scaled(36)}
            // In the open chat drawer's header the menu would open off-screen above.
            placement={isMobile && chatOpen ? "bottom" : "top"}
            onAddImage={onAddImage}
            onTogglePause={showPause ? onTogglePause : undefined}
            isPaused={isPaused}
            actionButtonStyle={actionButtonStyle}
            showLanguage={showLanguage}
            onCallGenie={onCallGenie}
            onEmailGenie={onEmailGenie}
            emailActionLabel={emailActionLabel}
            onEmailSummary={onEmailSummary}
            onTalkToHuman={onTalkToHuman}
            emailSummaryLabel={emailSummaryLabel}
            addToHomeScreen={addToHomeScreen}
          />
        )}

        {/* Slot: Resources — documents + links the visitor can take away.
            Only shown when the Genie actually has resources configured, and
            available both before and during a conversation. */}
        {showResources && (
          <MagicalIconButton
            onClick={() => onOpenResources?.()}
            brandColor={brandColor}
            size={scaled(36)}
            ariaLabel="Resources"
            label="Resources"
            hoverHint="Documents and links to take away"
            hintPlacement="top"
            idleBackgroundColor={actionButtonStyle?.idleBackgroundColor}
            activeBackgroundColor={actionButtonStyle?.activeBackgroundColor}
            iconColorOverride={actionButtonStyle?.iconColor}
          >
            <Paperclip size={scaled(16)} strokeWidth={2} />
          </MagicalIconButton>
        )}

        {/* Slot 3: Voice (when connected) or About (pre-connect) */}
        {voiceConnected ? (
          <MagicalIconButton
            onClick={() => void onToggleAgentVoice()}
            brandColor={brandColor}
            size={scaled(36)}
            ariaLabel={isAgentVoiceMuted ? "Play voice" : "Mute voice"}
            label={isAgentVoiceMuted ? "Unmute Genie" : "Mute Genie"}
            activeState={isAgentVoiceMuted ? "danger" : null}
            hoverHint={isAgentVoiceMuted ? "Hear your Genie speak" : "Mute your Genie's voice"}
            hintPlacement="top"
            idleBackgroundColor={actionButtonStyle?.idleBackgroundColor}
            activeBackgroundColor={actionButtonStyle?.activeBackgroundColor}
            iconColorOverride={actionButtonStyle?.iconColor}
          >
            {isAgentVoiceMuted ? (
              <VolumeX size={scaled(16)} strokeWidth={2} />
            ) : (
              <Volume2 size={scaled(16)} strokeWidth={2} />
            )}
          </MagicalIconButton>
        ) : showInfo ? (
          <div className="relative inline-flex flex-col items-center">
            <InfoPopover
              variant="modal"
              brandColor={brandColor}
              triggerPx={scaled(36)}
              title={infoTitle}
              description={infoDescription}
              infoLinks={infoLinks}
              qrLogoUrl={qrLogoUrl}
              idleBackgroundColor={actionButtonStyle?.idleBackgroundColor}
              iconColor={actionButtonStyle?.iconColor}
            />
            <span
              className="pointer-events-none mt-1 text-center text-[11px] font-medium leading-snug text-[#7C8B9C]"
              aria-hidden
            >
              About
            </span>
          </div>
        ) : null}

        {/* Slot: Get a summary — quiet, always-there before/after a conversation.
            Lets visitors leave their email (and optionally name/phone) so we can
            send a recap or follow up, without the Genie having to capture it by ear. */}
        {showSummary && (
          <div className="relative inline-flex flex-col items-center">
            <SummaryCaptureButton
              brandColor={brandColor}
              secondaryColor={secondaryColor}
              agentId={agentId}
              agentUserId={agentUserId}
              triggerPx={scaled(36)}
              onCaptured={onSummaryCaptured}
            />
            <span
              className="pointer-events-none mt-1 text-center text-[11px] font-medium leading-snug text-[#7C8B9C]"
              aria-hidden
            >
              Summary
            </span>
          </div>
        )}

        {/* Slot: Details — the consolidated about/links/resources/summary entry.
            Folded into the row on mobile (instead of a floating right-edge tab
            that overlaps the avatar), painted with the same branded book icon. */}
        {showDetails && (
          <MagicalIconButton
            onClick={() => onOpenDetails?.()}
            brandColor={brandColor}
            secondaryColor={secondaryColor}
            size={scaled(36)}
            ariaLabel="Open details — about, links, resources & summary"
            label="Details"
            hoverHint="About, links & resources"
            hintPlacement="top"
            idleBackgroundColor={actionButtonStyle?.idleBackgroundColor}
            activeBackgroundColor={actionButtonStyle?.activeBackgroundColor}
            iconColorOverride={actionButtonStyle?.iconColor}
          >
            <GenieIcon
              name="book-open"
              size={scaled(18)}
              color={actionButtonStyle?.iconColor || brandColor}
            />
          </MagicalIconButton>
        )}
      </div>
    </div>
  );
};

export default VoiceSessionActionRow;
